/**
 * DTO mirrors of the operations Reminders v2 backend (spec §13).
 *
 * These mirror the Prisma model + service shapes returned by
 * `apps/operations/src/modules/reminders/`. Kept structural so we can evolve
 * the backend without lock-step type generation.
 */

// ─── Enums (mirror Prisma enum literals) ──────────────────────────────────────

export type ReminderType =
  | 'JOB_CONFIRMATION'
  | 'JOB_REMINDER'
  | 'ESTIMATE_FOLLOWUP'
  | 'PROPOSAL_FOLLOWUP'
  | 'UNPAID_INVOICE'
  | 'UNPAID_DEPOSIT'
  | 'REVIEW_REQUEST'
  | 'PARTS_ORDERED'
  | 'PARTS_ARRIVED';

export type ReminderTargetType = 'APPOINTMENT' | 'ESTIMATE' | 'PROPOSAL' | 'INVOICE' | 'JOB';

export type ReminderStatus = 'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED';

export type ReminderCompletionReason = 'ACTION_TAKEN' | 'EXPIRED' | 'MANUAL' | 'ENTITY_VOIDED';

export type ReminderChannel = 'SMS' | 'EMAIL' | 'VOICE';

export type ReminderStepState =
  | 'SCHEDULED'
  | 'SENDING'
  | 'SENT'
  | 'DELIVERED'
  | 'ENGAGED'
  | 'COMPLETED'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'FAILED';

// ─── Sequence states (UI projection over status + pausedAt) ──────────────────

/**
 * Reminders Queue spec §13.2 surfaces a binary "ACTIVE / PAUSED" sequence
 * state distinct from the persisted ReminderStatus. Computed by
 * `getSequenceState()` from `status` + `pausedAt`.
 */
export type ReminderSequenceState = 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED';

// ─── Step events (append-only on ReminderStep.events) ────────────────────────

export interface ReminderStepEvent {
  type: 'queued' | 'sent' | 'delivered' | 'opened' | 'clicked' | 'replied' | 'failed' | string;
  at: string;
  meta?: Record<string, unknown>;
}

// ─── Models ──────────────────────────────────────────────────────────────────

export interface ReminderStep {
  id: string;
  tenantId: string;
  reminderId: string;
  sequenceIndex: number;
  scheduledAt: string;
  originalScheduledAt: string;
  channel: ReminderChannel;
  state: ReminderStepState;
  providerMessageId: string | null;
  renderedSubject: string | null;
  renderedBody: string | null;
  templateKey: string | null;
  aiGenerated: boolean;
  events: ReminderStepEvent[] | null;
  attemptCount: number;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Reminder {
  id: string;
  tenantId: string;
  policyId: string | null;
  type: ReminderType;
  targetType: ReminderTargetType;
  targetId: string;
  contactId: string | null;
  status: ReminderStatus;
  completedAt: string | null;
  completionReason: ReminderCompletionReason | null;
  aiContext: Record<string, unknown> | null;
  propensityScore: number | null;
  propensityUpdatedAt: string | null;
  pausedAt: string | null;
  cancelledAt: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReminderWithSteps extends Reminder {
  steps: ReminderStep[];
}

export interface ReminderListItem extends Reminder {
  contactName: string | null;
}

export interface ReminderQueueItem extends ReminderWithSteps {
  contactName: string | null;
}

export function reminderContactLabel(
  reminder: Pick<ReminderListItem, 'contactId' | 'contactName'>,
): string {
  return reminder.contactName ?? (reminder.contactId ? 'Unknown customer' : 'No contact');
}

// ─── Audit log ──────────────────────────────────────────────────────────────

export type ReminderAuditAction =
  | 'CREATE'
  | 'EDIT_STEP'
  | 'SKIP_NEXT_STEP'
  | 'SEND_NOW'
  | 'PAUSE'
  | 'RESUME'
  | 'CANCEL'
  | 'COMPLETE_FROM_EXTERNAL_ACTION'
  | 'POLICY_OVERRIDE'
  | 'STEP_DISPATCHED'
  | 'STEP_FAILED'
  | string;

export type ReminderAuditActorType = 'USER' | 'SYSTEM' | 'OUTBOX_EVENT';

export interface ReminderAuditEntry {
  id: string;
  tenantId: string;
  reminderId: string;
  actorUserId: string | null;
  actorType: ReminderAuditActorType;
  action: ReminderAuditAction;
  oldValue: unknown;
  newValue: unknown;
  reason: string | null;
  createdAt: string;
}

// ─── Preview ─────────────────────────────────────────────────────────────────

export interface ReminderPreviewNextStep {
  step: ReminderStep | null;
  rendered: {
    subject?: string;
    body: string;
    channel: ReminderChannel;
  } | null;
}

// ─── Query / pagination ──────────────────────────────────────────────────────

export interface ReminderListFilters {
  type?: ReminderType;
  status?: ReminderStatus;
  contactId?: string;
  targetType?: ReminderTargetType;
  targetId?: string;
}

export interface ReminderListParams extends ReminderListFilters {
  cursor?: string;
  limit?: number;
}

export interface ReminderListResponse {
  data: ReminderListItem[];
  nextCursor: string | null;
}

// ─── DTOs (mirror request bodies) ───────────────────────────────────────────

export interface EditReminderStepDto {
  channel?: ReminderChannel;
  scheduledAt?: string;
  renderedSubject?: string;
  renderedBody?: string;
  reason?: string;
}

export interface ReminderReasonDto {
  reason?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function getSequenceState(reminder: Reminder): ReminderSequenceState {
  if (reminder.status === 'CANCELLED') return 'CANCELLED';
  if (reminder.status === 'COMPLETED') return 'COMPLETED';
  if (reminder.status === 'EXPIRED') return 'EXPIRED';
  if (reminder.pausedAt) return 'PAUSED';
  return 'ACTIVE';
}

export function isTerminal(reminder: Reminder): boolean {
  return (
    reminder.status === 'CANCELLED' ||
    reminder.status === 'COMPLETED' ||
    reminder.status === 'EXPIRED'
  );
}

export function nextScheduledStep(steps: ReminderStep[] | undefined | null): ReminderStep | null {
  if (!steps || steps.length === 0) return null;
  return (
    steps
      .filter((step) => step.state === 'SCHEDULED')
      .sort((a, b) => a.sequenceIndex - b.sequenceIndex)[0] ?? null
  );
}

export function pastSteps(steps: ReminderStep[] | undefined | null): ReminderStep[] {
  if (!steps) return [];
  const past = steps.filter(
    (step) =>
      step.state === 'SENT' ||
      step.state === 'DELIVERED' ||
      step.state === 'ENGAGED' ||
      step.state === 'COMPLETED' ||
      step.state === 'FAILED' ||
      step.state === 'EXPIRED' ||
      step.state === 'CANCELLED',
  );
  return past.sort((a, b) => a.sequenceIndex - b.sequenceIndex);
}

export function upcomingSteps(steps: ReminderStep[] | undefined | null): ReminderStep[] {
  if (!steps) return [];
  return steps
    .filter((step) => step.state === 'SCHEDULED' || step.state === 'SENDING')
    .sort((a, b) => a.sequenceIndex - b.sequenceIndex);
}

/** Aggregate engagement counters from a step's events array (spec §13.2). */
export function stepEngagement(step: ReminderStep): {
  opens: number;
  clicks: number;
  replies: number;
} {
  const events = Array.isArray(step.events) ? step.events : [];
  let opens = 0;
  let clicks = 0;
  let replies = 0;
  for (const event of events) {
    if (event.type === 'opened') opens += 1;
    else if (event.type === 'clicked') clicks += 1;
    else if (event.type === 'replied') replies += 1;
  }
  return { opens, clicks, replies };
}

export function reminderEngagement(reminder: ReminderWithSteps): {
  opens: number;
  clicks: number;
  replies: number;
} {
  const totals = { opens: 0, clicks: 0, replies: 0 };
  for (const step of reminder.steps ?? []) {
    const counts = stepEngagement(step);
    totals.opens += counts.opens;
    totals.clicks += counts.clicks;
    totals.replies += counts.replies;
  }
  return totals;
}

// ─── Display helpers ─────────────────────────────────────────────────────────

export const REMINDER_TYPE_LABELS: Record<ReminderType, string> = {
  JOB_CONFIRMATION: 'Job Confirmation',
  JOB_REMINDER: 'Job Reminder',
  ESTIMATE_FOLLOWUP: 'Estimate Follow-up',
  PROPOSAL_FOLLOWUP: 'Proposal Follow-up',
  UNPAID_INVOICE: 'Unpaid Invoice',
  UNPAID_DEPOSIT: 'Unpaid Deposit',
  REVIEW_REQUEST: 'Review Request',
  PARTS_ORDERED: 'Parts Ordered',
  PARTS_ARRIVED: 'Parts Arrived',
};

export const REMINDER_TARGET_LABELS: Record<ReminderTargetType, string> = {
  APPOINTMENT: 'Appointment',
  ESTIMATE: 'Estimate',
  PROPOSAL: 'Proposal',
  INVOICE: 'Invoice',
  JOB: 'Job',
};

export const REMINDER_STATUS_LABELS: Record<ReminderStatus, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  COMPLETED: 'Completed',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
};

export const CHANNEL_LABELS: Record<ReminderChannel, string> = {
  SMS: 'SMS',
  EMAIL: 'Email',
  VOICE: 'Voice',
};
